import React, {useMemo, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import AstroCompo from './compo/AstroCompo';
import {ic, messiers, misc, ngc, planets, setup} from './meta/astro';

function toggleItem(set, item) {
    const next = new Set(set);
    next.has(item) ? next.delete(item) : next.add(item);
    return next;
}

export default function Astro() {
    const [selectedTypes, setSelectedTypes] = useState(new Set());
    const [selectedCatalogs, setSelectedCatalogs] = useState(new Set());

    const sections = useMemo(() => ([
        {id: 'messiers', title: 'Messier', accent: '#2b6cb0', items: messiers},
        {id: 'ngc', title: 'NGC', accent: '#1f9d8f', items: ngc},
        {id: 'ic', title: 'IC', accent: '#6b46c1', items: ic},
        {id: 'planets', title: 'Planets', accent: '#d97706', items: planets},
        {id: 'misc', title: 'Misc', accent: '#059669', items: misc},
        {id: 'setup', title: 'Setup', accent: '#4b5563', items: setup}
    ]).filter((section) => section.items.length > 0), []);

    const allTypes = useMemo(() => {
        const types = new Set();
        sections.forEach(s => s.items.forEach(item => { if (item.type) types.add(item.type); }));
        return [...types].sort();
    }, [sections]);

    const allCatalogs = useMemo(() => {
        const catalogs = new Set();
        sections.forEach(s => s.items.forEach(item => { if (item.catalog) catalogs.add(item.catalog); }));
        return [...catalogs].sort();
    }, [sections]);

    const filteredSections = useMemo(() => {
        return sections.map(section => ({
            ...section,
            items: section.items.filter(item => {
                const typeOk = selectedTypes.size === 0 || selectedTypes.has(item.type);
                const catalogOk = selectedCatalogs.size === 0 || selectedCatalogs.has(item.catalog);
                return typeOk && catalogOk;
            })
        })).filter(section => section.items.length > 0);
    }, [sections, selectedTypes, selectedCatalogs]);

    const filteredTotal = useMemo(() =>
        filteredSections.reduce((sum, s) => sum + s.items.length, 0),
    [filteredSections]);

    const hasFilter = selectedTypes.size > 0 || selectedCatalogs.size > 0;

    return (
        <Box sx={{width: '100%', px: {xs: 1, md: 2}, pb: 2}}>
            <Box
                sx={{
                    p: {xs: 2, md: 3},
                    mb: 3,
                    border: '1px solid #e2e8f0',
                    borderRadius: 3,
                    background: 'linear-gradient(120deg, #f8fbff 0%, #f4fdf9 100%)'
                }}
            >

                {/* Filtre par Type */}
                <Stack spacing={1} >
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        <Typography variant="caption" sx={{fontWeight: 600, color: 'text.secondary', minWidth: 52}}>
                            Type :
                        </Typography>
                        {allTypes.map(type => {
                            const selected = selectedTypes.has(type);
                            return (
                                <Chip
                                    key={type}
                                    label={type}
                                    size="small"
                                    clickable
                                    onClick={() => setSelectedTypes(prev => toggleItem(prev, type))}
                                    color={selected ? 'primary' : 'default'}
                                    variant={selected ? 'filled' : 'outlined'}
                                />
                            );
                        })}
                    </Stack>

                    {/* Filtre par Catalog */}
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        <Typography variant="caption" sx={{fontWeight: 600, color: 'text.secondary', minWidth: 52}}>
                            Catalog :
                        </Typography>
                        {allCatalogs.map(catalog => {
                            const selected = selectedCatalogs.has(catalog);
                            const section = sections.find(s => s.title.toLowerCase() === catalog.toLowerCase());
                            const accent = section?.accent || '#64748b';
                            return (
                                <Chip
                                    key={catalog}
                                    label={catalog}
                                    size="small"
                                    clickable
                                    onClick={() => setSelectedCatalogs(prev => toggleItem(prev, catalog))}
                                    variant={selected ? 'filled' : 'outlined'}
                                    sx={selected ? {
                                        backgroundColor: accent,
                                        borderColor: accent,
                                        color: '#fff',
                                        '&:hover': {backgroundColor: accent, opacity: 0.9}
                                    } : {
                                        borderColor: accent,
                                        color: accent
                                    }}
                                />
                            );
                        })}
                    </Stack>

                    {hasFilter && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="caption" sx={{color: 'text.secondary'}}>
                                {filteredTotal} objet{filteredTotal > 1 ? 's' : ''} affiché{filteredTotal > 1 ? 's' : ''}
                            </Typography>
                            <Chip
                                label="Effacer les filtres"
                                size="small"
                                onClick={() => { setSelectedTypes(new Set()); setSelectedCatalogs(new Set()); }}
                                sx={{cursor: 'pointer'}}
                            />
                        </Stack>
                    )}
                </Stack>
            </Box>

            {filteredSections.map((section) => (
                <Box key={section.id} sx={{mb: 4}}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            mb: 2,
                            color: section.accent,
                            borderLeft: `4px solid ${section.accent}`,
                            pl: 1.5
                        }}
                    >
                        {section.title} ({section.items.length})
                    </Typography>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, minmax(0, 1fr))',
                                xl: 'repeat(3, minmax(0, 1fr))'
                            },
                            gap: 2
                        }}
                    >
                        {section.items.map((tile, index) => (
                            <AstroCompo
                                key={`${section.id}-${tile.name}-${index}`}
                                tile={tile}
                                index={`${section.id}-${index}`}
                                accent={section.accent}
                            />
                        ))}
                    </Box>
                </Box>
            ))}

            {hasFilter && filteredSections.length === 0 && (
                <Box sx={{textAlign: 'center', py: 6, color: 'text.secondary'}}>
                    <Typography variant="body1">Aucun objet ne correspond aux filtres sélectionnés.</Typography>
                </Box>
            )}
        </Box>
    );
}


